/**
 * WalletConnect sometimes loses the wallet's reply on its way back to the app,
 * most often after the tab has been idle. The wallet has already submitted the
 * transaction and Hedera has executed it, but WalletConnect waits out its
 * 5-minute timeout and then fails with "Request expired". Waiting only for the
 * reply therefore leaves the app stuck, then reporting a failure for something
 * that happened, which invites a second, duplicate attempt.
 *
 * So while the wallet's reply is awaited, the mirror node is asked for the
 * transaction, and whichever confirms first wins. A transaction can only reach
 * Hedera until its valid start plus its valid duration, so once that window
 * (plus a margin) has passed without it, it was not executed.
 */

export const HEDERA_POLL_MS = 3_000;
/** How long after a wallet error to keep looking: the mirror shows a transaction 3–8 s after consensus. */
export const ERROR_GRACE_MS = 20_000;
/** Added to the end of the transaction's valid window, for mirror lag and clock skew. */
export const DEADLINE_MARGIN_MS = 30_000;

/** The transaction as the mirror node recorded it. */
export type HederaRecord = { result: string; entity_id: string | null };

export type WalletOutcome<R> = { via: 'wallet'; response: R } | { via: 'hedera'; record: HederaRecord };

/** The transaction's valid window closed without it reaching Hedera: definitely not executed. */
export class NotExecutedError extends Error {
  constructor() {
    super("The transaction wasn't executed: it expired before reaching Hedera. Nothing was sent, so it's safe to try again");
    this.name = 'NotExecutedError';
  }
}

/** Neither the wallet nor the mirror node could say whether it was executed. */
export class UnconfirmedError extends Error {
  constructor() {
    super("We couldn't confirm whether this went through. Check your wallet and this wallet's history before trying again");
    this.name = 'UnconfirmedError';
  }
}

/** A pause that can be cut short, so no timer outlives the call. */
const pause = (ms: number) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const done = new Promise(resolve => (timer = setTimeout(resolve, ms)));
  return { done, cancel: () => clearTimeout(timer) };
};

/**
 * Runs `execute` (the wallet request) while asking `lookup` for the
 * transaction every `pollMs`. `lookup` returns the mirror's record, null while
 * the mirror doesn't have it, or throws when it can't tell.
 *
 * - The wallet replies: its response.
 * - The mirror has it: its record, even if the wallet's reply never comes.
 * - The wallet reports a rejection: that error, at once.
 * - Any other wallet error: that error, unless the mirror shows the
 *   transaction within `graceMs` (it may have been submitted just before).
 * - `deadline` (ms) passes: NotExecutedError, or UnconfirmedError if the
 *   mirror couldn't be read.
 */
export async function executeWithHederaCheck<R>(
  execute: () => Promise<R>,
  lookup: () => Promise<HederaRecord | null>,
  deadline: number,
  {
    isRejection = () => false,
    pollMs = HEDERA_POLL_MS,
    graceMs = ERROR_GRACE_MS,
  }: { isRejection?: (error: unknown) => boolean; pollMs?: number; graceMs?: number } = {},
): Promise<WalletOutcome<R>> {
  let reply: { response: R } | { error: unknown } | undefined;
  const replied = execute().then(
    response => (reply = { response }),
    error => (reply = { error }),
  );
  let errorAt: number | undefined;

  for (;;) {
    const wait = pause(pollMs);
    await Promise.race([wait.done, replied]);
    wait.cancel();

    if (reply && 'response' in reply) return { via: 'wallet', response: reply.response };
    if (reply && 'error' in reply) {
      if (isRejection(reply.error)) throw reply.error;
      errorAt ??= Date.now();
    }

    const record = await lookup().catch(() => undefined);
    if (record) return { via: 'hedera', record };

    if (reply && 'error' in reply && Date.now() - errorAt! >= graceMs) throw reply.error;
    if (Date.now() >= deadline) throw record === null ? new NotExecutedError() : new UnconfirmedError();
  }
}
